import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';
import type { ActivityEntry } from '../components/ActivityCell';

export interface FlowPathOffer {
  id: string;
  flow_path_id: string;
  offer_id: string;
  position: number;
  weight: number;
  offer_name?: string;
  offer_url?: string;
}

export interface FlowPath {
  id: string;
  /** Paths hang off a rule group, which may be shared by several flows. */
  rule_group_id: string;
  landing_page_id: string | null;
  weight: number;
  position: number;
  lp_name?: string;
  lp_url?: string;
  offers: FlowPathOffer[];
}

export interface RuleCondition {
  field: string;
  operator: 'is' | 'is_not';
  values: string[];
}

export interface FlowRuleGroup {
  activity_status?: ActivityEntry | null;
  id: string;
  seq?: number;
  ref?: string;
  workspace_id: string;
  name: string;
  notes?: string | null;
  conditions: RuleCondition[];
  /** From the flow's assignment, not the shared group — the same group can sit at a
   *  different position/weight in another flow. */
  position: number;
  /** Relative share among the flow's groups whose conditions also match this visit. */
  weight: number;
  is_workspace_fallback?: boolean;
  /** Tried before the workspace fallback when none of this group's paths can take the click. */
  fallback_offer_id?: string | null;
  /** How many flows use this group. >1 means an edit here changes other flows too. */
  used_by_flow_count?: number;
  paths: FlowPath[];
}

/** One cluster of structurally identical groups that could be merged into one. */
export interface MergeCluster {
  keep: FlowRuleGroup;
  duplicates: FlowRuleGroup[];
  flows_affected: number;
}

export interface Flow {
  activity_status?: ActivityEntry | null;
  id: string;
  seq?: number;
  ref?: string;
  workspace_id: string;
  tag_ids?: string[];
  name: string;
  notes?: string | null;
  traffic_source_id: string | null;
  ad_account_id?: string | null;
  traffic_cap_limit: number;
  daily_conversion_cap?: number;
  status: string;
  created_at: string;
  updated_at: string;
  paths?: FlowPath[];
  rule_groups?: FlowRuleGroup[];
  traffic_source?: { id: string; name: string } | null;
}

export interface FlowUrlSet {
  campaign_url: string;
  paths: Array<{
    path_id: string;
    weight: number;
    type: 'landing_page' | 'direct';
    landing_page?: string;
    click_url?: string;
    click_through_urls?: Array<{
      offer_id: string;
      offer_name: string;
      position: number;
      url: string;
    }>;
    offers?: Array<{
      offer_id: string;
      offer_name: string;
      weight: number;
    }>;
  }>;
}

export interface FlowTrackingUrls {
  available_domains?: string[];
  domain_urls: Record<string, FlowUrlSet>;
}

export const flowsService = {
  query: async (filters: { limit?: number; offset?: number; search?: string; where?: WhereCondition ; order_by?: string; order?: 'asc' | 'desc' } = {}): Promise<{ data: Flow[]; total: number }> => {
    const where: WhereCondition | undefined = filters.where ?? (filters.search
      ? { flow: { name: { op: 'ilike', value: `%${filters.search}%` } } } : undefined);
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: filters.order_by ?? 'flow.created_at',
      order: filters.order ?? 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<Flow>>('/flows/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<Flow> => {
    const response = await api.get<ApiResponse<Flow>>(`/flows/${id}`);
    return response.data.data!;
  },

  create: async (
    data: Partial<Omit<Flow, 'rule_groups'>> & { rule_groups?: Array<{ id: string; weight?: number }> },
  ): Promise<Flow> => {
    const response = await api.post<ApiResponse<Flow>>('/flows', data);
    return response.data.data!;
  },

  update: async (
    id: string,
    data: Partial<Omit<Flow, 'rule_groups'>> & { rule_groups?: Array<{ id: string; weight?: number }> },
  ): Promise<Flow> => {
    const response = await api.patch<ApiResponse<Flow>>(`/flows/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/flows/${id}`);
  },

  unarchive: async (id: string): Promise<Flow> => {
    const response = await api.patch<ApiResponse<Flow>>(`/flows/${id}/unarchive`);
    return response.data.data!;
  },

  getTrackingUrls: async (id: string): Promise<FlowTrackingUrls> => {
    const response = await api.get<ApiResponse<FlowTrackingUrls>>(`/flows/${id}/tracking-urls`);
    return response.data.data!;
  },

  // ── Rule group library (groups shared across flows) ──

  /** Searchable + paged. The server bounds `limit`, so this stays cheap as groups grow. */
  // `workspaceIds` pins the query to those workspaces instead of the header's selection.
  listRuleGroups: async (params: { search?: string; limit?: number; offset?: number; flow_ids?: string[]; traffic_source_ids?: string[]; network_ids?: string[]; order_by?: string; order?: 'asc' | 'desc' } = {}, workspaceIds?: string[]): Promise<{ data: FlowRuleGroup[]; total: number }> => {
    const response = await api.post<ApiResponse<FlowRuleGroup[]> & { _meta?: { total: number } }>(
      '/rule-groups/query', params, workspaceIds?.length ? { params: { workspaceIds: workspaceIds.join(',') } } : undefined);
    return { data: response.data.data || [], total: response.data._meta?.total ?? 0 };
  },

  /** Which flows use a group — what the "used by N flows" warning lists. */
  flowsUsingGroup: async (groupId: string): Promise<Array<{ id: string; name: string }>> => {
    const response = await api.get<ApiResponse<Array<{ id: string; name: string }>>>(`/rule-groups/${groupId}/flows`);
    return response.data.data || [];
  },

  previewMerge: async (): Promise<{ clusters: MergeCluster[]; groups_removed: number; flows_affected: number }> => {
    const response = await api.post<ApiResponse<{ clusters: MergeCluster[]; groups_removed: number; flows_affected: number }>>('/rule-groups/merge/preview', {});
    return response.data.data!;
  },

  applyMerge: async (keepId: string, mergeIds: string[]): Promise<{ merged: number; flows_repointed: number }> => {
    const response = await api.post<ApiResponse<{ merged: number; flows_repointed: number }>>('/rule-groups/merge/apply', { keep_id: keepId, merge_ids: mergeIds });
    return response.data.data!;
  },

  // ── Rule group editing (a group owns its own paths/offers) ──

  getRuleGroup: async (groupId: string): Promise<FlowRuleGroup> => {
    const response = await api.get<ApiResponse<FlowRuleGroup>>(`/rule-groups/${groupId}`);
    return response.data.data!;
  },

  createRuleGroup: async (data: { name?: string; conditions?: RuleCondition[]; ref?: string; notes?: string | null; fallback_offer_id?: string | null; workspace_id?: string }): Promise<FlowRuleGroup> => {
    const response = await api.post<ApiResponse<FlowRuleGroup>>('/rule-groups', data);
    return response.data.data!;
  },

  editRuleGroup: async (groupId: string, data: { name?: string; conditions?: RuleCondition[]; ref?: string; notes?: string | null; fallback_offer_id?: string | null }): Promise<FlowRuleGroup> => {
    const response = await api.patch<ApiResponse<FlowRuleGroup>>(`/rule-groups/${groupId}`, data);
    return response.data.data!;
  },

  /** Deep-copy a group so a duplicated flow gets independent routing. */
  cloneRuleGroup: async (groupId: string, name?: string): Promise<FlowRuleGroup> => {
    const response = await api.post<ApiResponse<FlowRuleGroup>>(`/rule-groups/${groupId}/clone`, name ? { name } : {});
    return response.data.data!;
  },

  deleteRuleGroup: async (groupId: string): Promise<void> => {
    await api.delete(`/rule-groups/${groupId}`);
  },

  /** Atomic full replace of a group's paths — never leaves shared routing empty. */
  replaceGroupPaths: async (groupId: string, paths: Array<{ landing_page_id: string | null; weight: number; offers: Array<{ offer_id: string; position: number; weight: number }> }>): Promise<FlowRuleGroup> => {
    const response = await api.put<ApiResponse<FlowRuleGroup>>(`/rule-groups/${groupId}/paths`, { paths });
    return response.data.data!;
  },
};
